import * as React from 'react';
import { useMemo, useEffect } from 'react';
import { DatePicker, Row, Select, Input, Form } from 'antd';
import moment from 'moment-timezone';
import { nullSafeGet } from '../../utils/DataAccessUtils';

export const DATE_FORMAT = 'MMMM D, YYYY h:mm a';

/**
 * A thin wrapper that passes antd Form.Item's injected value/onChange down to
 * DatePicker, while rendering the timezone abbreviation badge inline.
 * Defined outside the parent component so it's stable (no recreation on re-render).
 */
const DatePickerWithBadge = React.forwardRef(
	(
		{ value, onChange, badge, disabledDate, disabledTime, showTime, format, showNow }: any,
		ref: any
	) => (
		<Input.Group compact style={{ display: 'flex' }}>
			<DatePicker
				ref={ref}
				value={value}
				onChange={onChange}
				disabledDate={disabledDate}
				disabledTime={disabledTime}
				style={{ width: '100%' }}
				showTime={showTime}
				format={format}
				showNow={showNow}
				allowClear={false}
			/>
			<span
				style={{
					display: 'inline-flex',
					alignItems: 'center',
					padding: '0 10px',
					background: '#fafafa',
					border: '1px solid #d9d9d9',
					borderLeft: 0,
					borderRadius: '0 2px 2px 0',
					whiteSpace: 'nowrap',
				}}
			>
				{badge}
			</span>
		</Input.Group>
	)
);

/**
 * antd v4 version of DateTimeWithTimeZone.
 * Renders a timezone selector + DatePicker with timezone abbreviation badge.
 * Uses Form.Item name= for both fields so they appear in onFinish values.
 * Requires the antd v4 `form` instance (from Form.useForm()) for Form.useWatch.
 */
export const DateTimeWithTimeZonev4 = ({
	form,
	location,
	rules = [],
	disabledDate,
	disabledTime,
	timeZoneName,
	dateName,
	displayName,
	showTime,
	initialTimeZone,
	help,
	required,
	showNow = true,
}: any) => {
	const myTimeZone = moment.tz.guess();
	const locationTimeZone = nullSafeGet('timeZoneId', location);
	const myTimeZoneAbbreviation = moment.tz(new Date(), myTimeZone).format('z');
	const localTimeZoneAbbreviation = locationTimeZone
		? moment.tz(new Date(), locationTimeZone).format('z')
		: null;

	const selectedTimeZone = Form.useWatch(timeZoneName, form) || locationTimeZone || myTimeZone;
	const selectedTimeZoneAbbreviation = useMemo(
		() => moment.tz(selectedTimeZone).format('z'),
		[selectedTimeZone]
	);

	const deducedShowNow = selectedTimeZone === myTimeZone && showNow;

	// Set a default timezone only if the parent form has not already provided one
	// (avoids overriding initialValues from the parent <Form>)
	useEffect(() => {
		if (!form.getFieldValue(timeZoneName)) {
			form.setFieldsValue({ [timeZoneName]: initialTimeZone || locationTimeZone || myTimeZone });
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<Row className="flex justify-between align-baseline" align="middle">
				<span style={{ paddingBottom: myTimeZone === locationTimeZone ? 8 : 0 }}>
					{required && (
						<span
							style={{
								color: '#ff4d4f',
								display: 'inline-block',
								marginRight: 4,
								fontSize: 14,
								lineHeight: 1,
							}}
						>
							*
						</span>
					)}
					{displayName}
				</span>
				<Form.Item name={timeZoneName} style={{ marginBottom: 0, paddingBottom: 0 }} noStyle>
					<Select
						style={{ marginLeft: 8, flex: 0, minWidth: 175, textAlign: 'right' }}
						bordered={false}
						disabled={myTimeZone === locationTimeZone}
					>
						<Select.Option value={myTimeZone}>My Time ({myTimeZoneAbbreviation})</Select.Option>
						{locationTimeZone && (
							<Select.Option value={locationTimeZone}>
								Location Time ({localTimeZoneAbbreviation})
							</Select.Option>
						)}
					</Select>
				</Form.Item>
			</Row>
			<Form.Item name={dateName} rules={rules} help={help} required={required}>
				<DatePickerWithBadge
					badge={selectedTimeZoneAbbreviation}
					disabledDate={disabledDate}
					disabledTime={disabledTime}
					showTime={showTime}
					format={DATE_FORMAT}
					showNow={deducedShowNow}
				/>
			</Form.Item>
		</>
	);
};
